import { transformVehicleManufacturer } from "services/api/catalog/vehicles/manufacturers/transformations";
import {
  FleetVehicle,
  FleetVehicleColor,
  FleetVehicleEditFormValues,
  FleetVehicleRow,
  FleetVehicleStatusDetailFormValues,
  FleetVehicleStatusHistoryRow,
  VehiclePrice,
} from "./types";
import { transformVehicleModel } from "services/api/catalog/vehicles/models/transformations";
import { transformVehicleType } from "services/api/catalog/vehicles/types/transformations";
import {
  transformVehicleLocation,
  transformVehicleLocationOnSave,
} from "./locations/transformations";
import {
  initialVehicleLocationForm,
  isVehicleLocationComplete,
} from "./locations/consts";
import { transformFleetVehicleServiceOnSave } from "./services/transformations";

/*---------------------------------------------------------------------------------------
    VEHICLE PRICE
  -----------------------------------------------------------------------------------------*/

export const transformVehiclePrice = (res: any): VehiclePrice => {
  return {
    netPrice: res?.netPrice ?? null,
    specialTax: res?.specialTax ?? null,
    ecoFee: res?.ecoFee ?? null,
    vehicleInVatSystem: res?.vehicleInVatSystem ?? false,
    sellingPrice: res?.sellingPrice ?? null,
    discountType: res?.discountType ?? null,
    discountValue: res?.discountValue ?? null,
    netDiscount: res?.netDiscount ?? null,
    totalPurchasePrice: res?.totalPurchasePrice ?? null,
    margin: res?.margin ?? null,
    marginPercentage: res?.marginPercentage ?? null,
    totalExpenses: res?.totalExpenses ?? null,
    netMargin: res?.netMargin ?? null,
    netMarginPercentage: res?.netMarginPercentage ?? null,
  };
};

/*---------------------------------------------------------------------------------------
    FLEET VEHICLE SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformFleetVehicleRow = (res: any): FleetVehicleRow => {
  return {
    id: res.id,
    name: res.name,
    picture: res.picture,
    vehicleManufacturer: res.vehicleManufacturer ? transformVehicleManufacturer(res.vehicleManufacturer) : null,
    vehicleModel: res.vehicleModel ? transformVehicleModel(res.vehicleModel) : null,
    vehicleType: res.vehicleType ? transformVehicleType(res.vehicleType) : null,
    vinNumber: res.vinNumber,
    registrationPlate: res.registrationPlate,
    registrationExpiration: res.registrationExpiration,
    kmTraveled: res.kmTraveled,
    firstRegistrationDate: res.firstRegistrationDate,
    vehicleColorId: res.vehicleColorId,
    colorName: res.colorName,
    status: res.status ?? null,
    location: res.location ? transformVehicleLocation(res.location) : null,
    price: transformVehiclePrice(res.price),
  };
};

/*---------------------------------------------------------------------------------------
    FLEET VEHICLE REGISTRATION
  -----------------------------------------------------------------------------------------*/

export const transformFleetVehicleRegistrationOnCreate = (
  registrationPlate: string,
  registrationExpiration: Date | null
): any => ({
  registrationPlate,
  registrationExpiration,
});

/*---------------------------------------------------------------------------------------
    FLEET VEHICLE CREATE
  -----------------------------------------------------------------------------------------*/

export const transformFleetVehicleOnCreate = (
  formValues: FleetVehicleEditFormValues
): any => ({
  vehicleTypeId: formValues.vehicleTypeId,
  color: null,
  firstRegistrationDate: null,
  registrationPlate: null,
  registrationExpiration: null,
  vinNumber: null,
  kmTraveled: null,
  netPrice: null,
  specialTax: null,
  ecoFee: null,
  vehicleInVatSystem: null,
  sellingPrice: null,
  ownerTypeId: null,
  ownerContactId: null,
  purchaseDate: null,
  leaseActivationDate: null,
  warrantyExpirationDate: null,
  warrantyMileageLimit: null,
  autoInsuranceExpiryDate: null,
  autoInsuranceContactId: null,
  cascoExpiryDate: null,
  cascoContactId: null
});

/*---------------------------------------------------------------------------------------
     EDIT
  -----------------------------------------------------------------------------------------*/
export const transformFleetVehicleOnUpdate = (
  formValues: FleetVehicleEditFormValues
): any => {

  return ({
    id: formValues.id,
    vehicleTypeId: formValues.vehicleTypeId,
    vehicleColorId: formValues.vehicleColorId,
    colorName: formValues.colorName,
    firstRegistrationDate: formValues.firstRegistrationDate,
    registrationPlate: formValues.registrationPlate,
    registrationExpiration: formValues.registrationExpiration,
    vinNumber: formValues.vinNumber,
    kmTraveled: formValues.kmTraveled,
    vehicleInVatSystem: formValues.vehicleInVatSystem,
    netPrice: formValues.netPrice,
    specialTax: formValues.specialTax,
    ecoFee: formValues.ecoFee,
    sellingPrice: formValues.sellingPrice,
    discountType: formValues.discountType,
    discountValue: formValues.discountValue,
    netDiscount: formValues.netDiscount,
    totalPurchasePrice: calculateTotalPurchasePrice(formValues),
    margin: calculateMargin(formValues),
    marginPercentage: calculateMarginPercentage(formValues),
    ownerTypeId: formValues.ownerTypeId,
    ownerContactId: formValues.ownerContactId,
    purchaseDate: formValues.purchaseDate,
    leaseActivationDate: formValues.leaseActivationDate,
    warrantyExpirationDate: formValues.warrantyExpirationDate,
    warrantyMileageLimit: formValues.warrantyMileageLimit,
    autoInsuranceExpiryDate: formValues.autoInsuranceExpiryDate,
    autoInsuranceContactId: formValues.autoInsuranceContactId,
    cascoExpiryDate: formValues.cascoExpiryDate,
    cascoContactId: formValues.cascoContactId,
    status: formValues.status,
    // Lokacija se na backendu ne sprema kao polje vozila nego kao premještaj, i to samo
    // kad se stvarno razlikuje od trenutne. Neobavezna je, pa se šalje tek kad je potpuna.
    location: isVehicleLocationComplete(formValues.location)
      ? transformVehicleLocationOnSave(formValues.location!)
      : null,
  })
};




/*---------------------------------------------------------------------------------------
    INFO
  -----------------------------------------------------------------------------------------*/

export const transformFleetVehicle = (res: any): FleetVehicle => {

  return {
    id: res.id,
    defaultImage: res.defaultImage,
    vehicleCategory: res.vehicleCategory,
    vehicleColorId: res.vehicleColorId,
    colorName: res.colorName,
    created: res.created,
    cylinderCapacity: res.cylinderCapacity,
    firstRegistrationDate: res.firstRegistrationDate,
    fuelType: res.fuelType,
    hp: res.hp,
    kmTraveled: res.kmTraveled,
    kw: res.kw,
    name: res.name,
    registrationExpiration: res.registrationExpiration,
    registrationPlate: res.registrationPlate,
    secondaryFuelType: res.secondaryFuelType,
    price: transformVehiclePrice(res.price),
    vehicleManufacturer: res.vehicleManufacturer,
    vehicleModel: res.vehicleModel,
    vehicleType: res.vehicleType,
    vinNumber: res.vinNumber,
    ownerType: res.ownerType,
    ownerContact: res.ownerContact,
    purchaseDate: res.purchaseDate,
    leaseActivationDate: res.leaseActivationDate,
    warrantyExpirationDate: res.warrantyExpirationDate,
    warrantyMileageLimit: res.warrantyMileageLimit,
    autoInsuranceExpiryDate: res.autoInsuranceExpiryDate,
    autoInsuranceContactId: res.autoInsuranceContactId,
    autoInsuranceContact: res.autoInsuranceContact,
    cascoExpiryDate: res.cascoExpiryDate,
    cascoContact: res.cascoContact,
    status: res.status ?? null,
    location: res.location ? transformVehicleLocation(res.location) : null,
    pictures: res.pictures
  };
};

/*---------------------------------------------------------------------------------------
    EDIT
  -----------------------------------------------------------------------------------------*/
export const transformFleetVehicleEditFormValues = (
  res: FleetVehicle
): FleetVehicleEditFormValues => {
  return {
    id: res.id ?? "",
    vehicleCategoryId: res.vehicleCategory?.id ?? null,
    vehicleColorId: res.vehicleColorId,
    colorName: res.colorName,
    firstRegistrationDate: res.firstRegistrationDate,
    kmTraveled: res.kmTraveled,
    registrationExpiration: res.registrationExpiration,
    registrationPlate: res.registrationPlate,
    vinNumber: res.vinNumber,
    vehicleManufacturerId: res.vehicleManufacturer?.id,
    vehicleModelId: res.vehicleModel?.id,
    vehicleTypeId: res.vehicleType?.id,
    netPrice: res.price?.netPrice ?? null,
    discountType: res.price?.discountType ?? null,
    discountValue: res.price?.discountValue ?? null,
    netDiscount: res.price?.netDiscount ?? null,
    specialTax: res.price?.specialTax ?? null,
    ecoFee: res.price?.ecoFee ?? null,
    vehicleInVatSystem: res.price?.vehicleInVatSystem ?? true,
    sellingPrice: res.price?.sellingPrice ?? null,
    ownerTypeId: res.ownerType?.id,
    ownerContactId: res.ownerContact?.id,
    purchaseDate: res.purchaseDate,
    leaseActivationDate: res.leaseActivationDate,
    warrantyExpirationDate: res.warrantyExpirationDate,
    warrantyMileageLimit: res.warrantyMileageLimit,
    autoInsuranceExpiryDate: res.autoInsuranceExpiryDate,
    autoInsuranceContactId: res.autoInsuranceContactId,
    cascoExpiryDate: res.cascoExpiryDate,
    cascoContactId: res.cascoContact?.id ?? null,
    status: res.status ?? null,
    // Uređivanje kreće od zatečene lokacije, da spremanje bez diranja ne stvori premještaj.
    location: res.location
      ? {
          ...initialVehicleLocationForm,
          locationType:
            res.location.locationType ?? initialVehicleLocationForm.locationType,
          officeId: res.location.officeId ?? null,
          contactId: res.location.contactId ?? null,
          addressLine1: res.location.addressLine1 ?? null,
          addressCity: res.location.addressCity ?? null,
          addressPostalCode: res.location.addressPostalCode ?? null,
          addressCountry: res.location.addressCountry ?? null,
        }
      : null,
    pictures: res.pictures,

  };
};

/*---------------------------------------------------------------------------------------
    BOJA
  -----------------------------------------------------------------------------------------*/
export const transformFleetVehicleColor = (res: any): FleetVehicleColor => {
  return {
    id: res.id,
    name: res.name,
  };
};

/*---------------------------------------------------------------------------------------
    IZRAČUN CIJENA
  -----------------------------------------------------------------------------------------*/

const VAT_RATE = 0.25;

/** Trošak nabave bez PDV-a: neto + PPMV + naknada za zaštitu okoliša */
const calculatePurchaseCost = (formValues: FleetVehicleEditFormValues): number | null => {
  const net = formValues.netPrice ?? 0;
  if (net === 0) return null;
  return net + (formValues.specialTax ?? 0) + (formValues.ecoFee ?? 0);
};

/** Ukupna nabavna cijena: trošak nabave + PDV (ako u sustavu) */
export const calculateTotalPurchasePrice = (formValues: FleetVehicleEditFormValues): number | null => {
  const cost = calculatePurchaseCost(formValues);
  if (cost == null) return null;
  const vat = formValues.vehicleInVatSystem ? (formValues.netPrice ?? 0) * VAT_RATE : 0;
  return cost + vat;
};

/**
 * PDV sadržan u prodajnoj cijeni s popustom. Prodajna cijena je bruto; izvan sustava PDV-a nema ga.
 * PPMV ne ulazi u osnovicu PDV-a, pa se izdvaja prije računanja.
 */
export const calculateSellingVat = (formValues: FleetVehicleEditFormValues): number => {
  if (!formValues.vehicleInVatSystem || !formValues.sellingPrice) return 0;
  const sellingWithDiscount = formValues.sellingPrice - (formValues.netDiscount ?? 0);
  const vatInclusive = Math.max(0, sellingWithDiscount - (formValues.specialTax ?? 0));
  return Math.round((vatInclusive - vatInclusive / (1 + VAT_RATE)) * 100) / 100;
};

/**
 * Marža neto na neto: prodajna cijena - popust - PDV u prodajnoj - trošak nabave.
 * PDV na nabavu je odbitan pa nije trošak.
 */
export const calculateMargin = (formValues: FleetVehicleEditFormValues): number | null => {
  const cost = calculatePurchaseCost(formValues);
  if (cost == null || !formValues.sellingPrice) return null;
  const discount = formValues.netDiscount ?? 0;
  const margin = formValues.sellingPrice - discount - calculateSellingVat(formValues) - cost;
  return Math.round(margin * 100) / 100;
};

/** Marža u postotku: (marža / trošak nabave) * 100 */
export const calculateMarginPercentage = (formValues: FleetVehicleEditFormValues): number | null => {
  const cost = calculatePurchaseCost(formValues);
  const margin = calculateMargin(formValues);
  if (cost == null || cost === 0 || margin == null) return null;
  return Math.round((margin / cost) * 10000) / 100;
};

/*---------------------------------------------------------------------------------------
    STATUS DETAILS / HISTORY
  -----------------------------------------------------------------------------------------*/

/** Detalji uz promjenu statusa — prazna forma šalje samo status, bez praznih polja. */
export const transformFleetVehicleStatusDetailOnSave = (
  formValues?: FleetVehicleStatusDetailFormValues | null
): any => {
  if (!formValues) return {};

  return {
    validFrom: formValues.validFrom,
    validUntil: formValues.validUntil,
    note: formValues.note,
    contactId: formValues.contactId,
    driverId: formValues.driverId,
    vehicleDamageId: formValues.vehicleDamageId,
    offerSaleId: formValues.offerSaleId,
    kmTraveled: formValues.kmTraveled,
    depositAmount: formValues.depositAmount,
    // Premještaj koji prati status (odlazak na servis, povratak u poslovnicu). Backend ga
    // upisuje u istoj transakciji; nepotpunu lokaciju odbija, pa se takva ne šalje.
    location: isVehicleLocationComplete(formValues.location)
      ? transformVehicleLocationOnSave(formValues.location!)
      : undefined,
    // Nalog se šalje samo kad ga status stvarno otvara — prazan blok bi backend pokušao upisati.
    service: formValues.service
      ? transformFleetVehicleServiceOnSave(
          formValues.service,
          formValues.service.documentUploads
        )
      : undefined,
  };
};

export const transformFleetVehicleStatusHistoryRow = (
  res: any
): FleetVehicleStatusHistoryRow => ({
  id: res.id,
  status: res.status,
  validFrom: res.validFrom ? new Date(res.validFrom) : null,
  validUntil: res.validUntil ? new Date(res.validUntil) : null,
  note: res.note ?? null,
  contactId: res.contactId ?? null,
  contactName: res.contactName ?? null,
  driverId: res.driverId ?? null,
  driverName: res.driverName ?? null,
  vehicleDamageId: res.vehicleDamageId ?? null,
  vehicleDamageName: res.vehicleDamageName ?? null,
  offerSaleId: res.offerSaleId ?? null,
  offerSaleNumber: res.offerSaleNumber ?? null,
  kmTraveled: res.kmTraveled ?? null,
  depositAmount: res.depositAmount ?? null,
  created: new Date(res.created),
  userCreatedName: res.userCreatedName ?? null,
});
